import type { ColDef, IServerSideDatasource } from "ag-grid-community";
import dayjs from "dayjs";
import { PanelRightIcon } from "lucide-react";
import React, { type ComponentPropsWithoutRef } from "react";

import { trpc } from "@blis/trpc/react";

import { Link } from "@/components/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AdminGrid,
  denseAdminGridTheme,
} from "@/features/admin/components/AdminGrid";
import { useAdminServerSideGrid } from "@/features/admin/utils/useAdminServerSideGrid";
import { IntegrationMessageSheet } from "./IntegrationMessageSheet";

export type IntegrationMessagesGridProps = {
  kind: "ITRANSPLANT";
};

type MessageRow = {
  id: number;
  createdAt: string | Date;
  status: "PENDING" | "SUCCESS" | "FAILED";
  attempt: number;
  responseStatus: number | null;
  accessionId: number | null;
  reportId: number | null;
  errorMessage: string | null;
};

export function IntegrationMessagesGrid({
  kind,
}: IntegrationMessagesGridProps) {
  const [selectedId, setSelectedId] = React.useState<number | null>(null);
  const dataSource = useDataSource(kind);
  const { gridProps } = useAdminServerSideGrid<MessageRow>({
    serverSideDatasource: dataSource,
    paginationPageSize: 100,
    paginationPageSizeSelector: [25, 50, 100, 500],
    refreshDeps: [kind],
  });
  const columnDefs = useColumnDefs(setSelectedId);

  return (
    <>
      <AdminGrid<MessageRow>
        {...gridProps}
        theme={denseAdminGridTheme}
        columnDefs={columnDefs}
        getRowId={({ data }) => String(data.id)}
        defaultColDef={{
          flex: 1,
          resizable: true,
          sortable: false,
          enableRowGroup: false,
          enablePivot: false,
          enableValue: false,
          filterParams: { maxNumConditions: 1 },
        }}
        suppressDragLeaveHidesColumns
        sideBar={{ toolPanels: ["filters"] }}
      />
      <IntegrationMessageSheet
        messageId={selectedId}
        onClose={() => setSelectedId(null)}
      />
    </>
  );
}

function useColumnDefs(onView: (id: number) => void): ColDef<MessageRow>[] {
  return React.useMemo(
    () =>
      [
        {
          colId: "leftAction",
          headerName: "",
          maxWidth: 34,
          pinned: "left",
          cellStyle: { padding: 0 },
          cellRenderer: ({ data }: { data?: MessageRow }) => {
            if (!data) return null;
            return (
              <Button
                size="icon-sm"
                variant="ghost"
                className="w-full h-full rounded-none text-muted-foreground"
                onPress={() => onView(data.id)}
                aria-label="View message details"
              >
                <PanelRightIcon />
              </Button>
            );
          },
          suppressHeaderMenuButton: true,
          resizable: false,
          sortable: false,
          filter: false,
          flex: undefined,
        },
        {
          field: "createdAt",
          headerName: "When",
          filter: "agDateColumnFilter",
          filterParams: {
            filterOptions: ["equals", "lessThan", "greaterThan", "inRange"],
            maxNumConditions: 1,
          },
          sortable: true,
          initialSort: "desc",
          cellClass: "font-mono text-xs",

          valueFormatter: ({ value }) =>
            value ? dayjs(value).format("YYYY-MM-DD HH:mm:ss") : "",
          minWidth: 180,
        },
        {
          field: "status",
          headerName: "Status",
          filter: "agSetColumnFilter",
          filterParams: { values: ["PENDING", "SUCCESS", "FAILED"] },
          cellRenderer: StatusBadge,
          minWidth: 130,
          maxWidth: 160,
        },
        {
          field: "attempt",
          headerName: "Attempt",
          filter: "agNumberColumnFilter",
          sortable: true,
          minWidth: 110,
          maxWidth: 130,
        },
        {
          field: "responseStatus",
          headerName: "HTTP",
          filter: "agNumberColumnFilter",
          sortable: true,
          minWidth: 100,
          maxWidth: 120,
          valueFormatter: ({ value }) => (value == null ? "—" : `${value}`),
        },
        {
          field: "accessionId",
          headerName: "Accession",
          filter: "agNumberColumnFilter",
          minWidth: 130,
          maxWidth: 160,
          cellRenderer: AccessionLink,
        },
        {
          field: "reportId",
          headerName: "Report",
          filter: "agNumberColumnFilter",
          minWidth: 110,
          maxWidth: 140,
          valueFormatter: ({ value }) => (value == null ? "—" : `${value}`),
        },
        {
          field: "errorMessage",
          headerName: "Message",
          flex: 2,
          minWidth: 200,
          tooltipField: "errorMessage",
        },
      ] as ColDef<MessageRow>[],
    [onView],
  );
}

function useDataSource(kind: "ITRANSPLANT"): IServerSideDatasource {
  const utils = trpc.useUtils();
  return React.useMemo(
    () => ({
      getRows: async (params) => {
        const { startRow, endRow, filterModel, sortModel } = params.request;
        try {
          const { data, totalCount } =
            await utils.integrations.listMessages.fetch({
              kind,
              take: (endRow ?? 0) - (startRow ?? 0),
              skip: startRow ?? 0,
              // biome-ignore lint/suspicious/noExplicitAny: ag-grid typings vs zod
              filterModel: filterModel as any,
              // biome-ignore lint/suspicious/noExplicitAny: ag-grid typings vs zod
              sortModel: sortModel as any,
            });
          params.success({ rowData: data, rowCount: totalCount });
        } catch (_err) {
          params.fail();
        }
      },
    }),
    [utils, kind],
  );
}

const AccessionLink = React.memo(({ value }: { value: number | null }) => {
  if (value == null) return <span>—</span>;
  return (
    <Link to="/accessions/$id" params={{ id: String(value) }}>
      {value}
    </Link>
  );
});

const StatusBadge = React.memo(({ value }: { value: string }) => {
  let color: ComponentPropsWithoutRef<typeof Badge>["color"] = "gray";
  switch (value) {
    case "SUCCESS":
      color = "green";
      break;
    case "FAILED":
      color = "red";
      break;
    case "PENDING":
      color = "amber";
      break;
  }
  return (
    <div className="flex h-full items-center">
      <Badge color={color} size="sm">
        {value}
      </Badge>
    </div>
  );
});
